import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { FileCheck, Loader2, Download, Filter, ShieldCheck, Users, AlertTriangle, X, ScrollText } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
    Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import api, { formatApiError } from "@/lib/api";
import { toast } from "@/components/ui/sonner";
import SignedConsents from "@/pages/admin/SignedConsents";

const DAY_OPTIONS = [
    { v: 7, l: "Last 7 days" },
    { v: 30, l: "Last 30 days" },
    { v: 90, l: "Last 90 days" },
    { v: 180, l: "Last 6 months" },
    { v: 365, l: "Last 12 months" },
];

const actionTone = (a) => {
    if (a.startsWith("verification.pii_reveal")) return "bg-destructive/10 text-destructive border-destructive/30";
    if (a === "verification.create") return "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border-emerald-500/30";
    if (a.startsWith("user.")) return "bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-500/30";
    if (a.startsWith("admin.")) return "bg-violet-500/15 text-violet-700 dark:text-violet-400 border-violet-500/30";
    return "bg-muted text-muted-foreground border-border";
};

export default function ComplianceConsole() {
    const [params, setParams] = useSearchParams();
    const activeTab = params.get("tab") === "consents" ? "consents" : "events";
    const setTab = (t) => setParams(t === "events" ? {} : { tab: t }, { replace: true });

    const [days, setDays] = useState(30);
    const [action, setAction] = useState("any");
    const [actorFilter, setActorFilter] = useState("");
    const [summary, setSummary] = useState(null);
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [exporting, setExporting] = useState(false);
    const [anomalies, setAnomalies] = useState([]);
    const [dismissedIds, setDismissedIds] = useState(new Set());

    const loadAnomalies = useCallback(async () => {
        try {
            const [{ data: an }, { data: dm }] = await Promise.all([
                api.get("/admin/compliance/anomalies"),
                api.get("/admin/compliance/anomalies/dismissed"),
            ]);
            setAnomalies(an.anomalies || []);
            setDismissedIds(new Set(dm.dismissed_ids || []));
        } catch {
            // Silent — anomalies are non-critical
        }
    }, []);
    useEffect(() => { loadAnomalies(); }, [loadAnomalies]);

    const dismissAnomaly = async (id) => {
        try {
            await api.post("/admin/compliance/anomalies/dismiss", { anomaly_id: id, reason: "Reviewed via console" });
            setDismissedIds((s) => new Set([...s, id]));
            toast.success("Alert dismissed.");
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    const visibleAnomalies = useMemo(
        () => anomalies.filter((a) => !dismissedIds.has(a.id)),
        [anomalies, dismissedIds],
    );

    const load = useCallback(async () => {
        try {
            setLoading(true);
            const params = { days, limit: 200 };
            if (action !== "any") params.action = action;
            if (actorFilter.trim()) params.actor = actorFilter.trim();
            const [summaryRes, eventsRes] = await Promise.all([
                api.get("/admin/compliance/summary", { params: { days } }),
                api.get("/admin/compliance/events", { params }),
            ]);
            setSummary(summaryRes.data);
            setEvents(eventsRes.data.results || []);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    }, [days, action, actorFilter]);

    useEffect(() => { load(); }, [load]);

    const doExport = async () => {
        try {
            setExporting(true);
            const params = new URLSearchParams({ days: String(days) });
            if (action !== "any") params.set("action", action);
            if (actorFilter.trim()) params.set("actor", actorFilter.trim());
            const res = await api.get(`/admin/compliance/export.csv?${params.toString()}`, {
                responseType: "blob",
            });
            const blob = new Blob([res.data], { type: "text/csv" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            const cd = res.headers["content-disposition"] || "";
            const m = cd.match(/filename="([^"]+)"/);
            a.download = m ? m[1] : `simonara-compliance-${Date.now()}.csv`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
            toast.success("Export downloaded — the download itself is audited.");
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setExporting(false);
        }
    };

    const watched = summary?.watched_actions || [];
    const byAction = useMemo(() => (summary?.by_action || []), [summary]);

    return (
        <div data-testid="admin-compliance-console">
            <div className="mb-6 flex items-end justify-between flex-wrap gap-3">
                <div>
                    <p className="overline">Command Center</p>
                    <h1 data-testid="dash-page-title" className="mt-1 font-heading font-medium text-2xl sm:text-3xl tracking-tight text-foreground">
                        Compliance Console
                    </h1>
                    <p className="text-sm text-muted-foreground mt-1">
                        SOC 2-ready view of sensitive audit events — PII reveals, admin changes, user account actions.
                    </p>
                </div>
                <div className="flex gap-2">
                    <button
                        onClick={async () => {
                            try {
                                const { data } = await api.post("/admin/compliance/digest/send");
                                toast.success(`Digest sent to ${data.recipients.length} platform admin${data.recipients.length === 1 ? "" : "s"}.`);
                            } catch (err) {
                                toast.error(formatApiError(err));
                            }
                        }}
                        data-testid="cc-send-digest"
                        className="btn-outline text-sm inline-flex items-center gap-1"
                    >
                        <ScrollText size={13} /> Send digest now
                    </button>
                    <button
                        onClick={doExport}
                        disabled={exporting || activeTab !== "events"}
                        className="btn-green text-sm inline-flex items-center gap-1 disabled:opacity-60"
                        data-testid="cc-export"
                    >
                        {exporting ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} />}
                        Export CSV (audited)
                    </button>
                </div>
            </div>

            {/* Tab strip — unifies audit-events view with Signed Consents. */}
            <div className="mb-5 flex gap-1 border-b border-border" role="tablist">
                {[
                    { k: "events",   label: "Audit events",   icon: FileCheck,  testId: "cc-tab-events" },
                    { k: "consents", label: "Signed consents", icon: ScrollText, testId: "cc-tab-consents" },
                ].map((t) => {
                    const Icon = t.icon;
                    const active = activeTab === t.k;
                    return (
                        <button
                            key={t.k}
                            role="tab"
                            aria-selected={active}
                            onClick={() => setTab(t.k)}
                            data-testid={t.testId}
                            className={`inline-flex items-center gap-1.5 px-3 py-2 text-sm border-b-2 -mb-px transition-colors ${
                                active
                                    ? "border-emerald-500 text-foreground font-semibold"
                                    : "border-transparent text-muted-foreground hover:text-foreground"
                            }`}
                        >
                            <Icon className="h-4 w-4" /> {t.label}
                        </button>
                    );
                })}
            </div>

            {activeTab === "consents" ? (
                // Signed Consents rendered inline as a tab of the Compliance Console.
                // The standalone /admin/signed-consents route still redirects here
                // via ?tab=consents so old links keep working.
                <div data-testid="cc-consents-pane"><SignedConsents /></div>
            ) : (
                <div data-testid="cc-events-pane">
            <div className="card-soft p-3 mb-4 flex items-start gap-2 bg-emerald-500/5 border-emerald-500/20">
                <ShieldCheck size={14} className="text-emerald-600 mt-0.5 shrink-0" />
                <p className="text-xs text-muted-foreground">
                    <b className="text-foreground">Auditor-ready view.</b> Every row here comes from the immutable
                    <span className="mono-score"> audit_log </span> collection. Exporting a CSV writes a
                    <span className="mono-score"> compliance.export_csv </span> entry to the log so exports are themselves auditable.
                </p>
            </div>

            {visibleAnomalies.length > 0 && (
                <div className="space-y-2 mb-4" data-testid="compliance-anomalies">
                    {visibleAnomalies.map((a) => (
                        <div
                            key={a.id}
                            data-testid={`anomaly-${a.kind}`}
                            className={`card-soft p-3 flex items-start gap-2 border ${a.severity === "high"
                                ? "border-destructive/40 bg-destructive/5"
                                : "border-amber-500/40 bg-amber-500/5"}`}
                        >
                            <AlertTriangle size={14} className={`mt-0.5 shrink-0 ${a.severity === "high" ? "text-destructive" : "text-amber-600"}`} />
                            <div className="flex-1 min-w-0">
                                <div className="text-xs font-semibold text-foreground">
                                    {a.label} <span className="text-[10px] font-normal text-muted-foreground">· {a.severity}</span>
                                </div>
                                <div className="text-xs text-muted-foreground mt-0.5">{a.detail}</div>
                            </div>
                            <button
                                onClick={() => dismissAnomaly(a.id)}
                                className="text-muted-foreground hover:text-foreground shrink-0"
                                data-testid={`anomaly-dismiss-${a.kind}`}
                                aria-label="Dismiss"
                            >
                                <X size={14} />
                            </button>
                        </div>
                    ))}
                </div>
            )}

            {/* Summary cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                <div className="card-soft p-4">
                    <div className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">Sensitive events</div>
                    <div className="mt-1 font-heading font-bold text-2xl text-foreground" data-testid="cc-total">{summary?.total_sensitive_events ?? "—"}</div>
                    <div className="text-[10px] text-muted-foreground mt-0.5">Last {days} days</div>
                </div>
                <div className="card-soft p-4">
                    <div className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">PII reveals</div>
                    <div className="mt-1 font-heading font-bold text-2xl text-destructive">
                        {byAction.find((a) => a.action === "verification.pii_reveal")?.count || 0}
                    </div>
                </div>
                <div className="card-soft p-4">
                    <div className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">User account changes</div>
                    <div className="mt-1 font-heading font-bold text-2xl text-amber-600">
                        {byAction.filter((a) => a.action.startsWith("user.")).reduce((s, a) => s + a.count, 0)}
                    </div>
                </div>
                <div className="card-soft p-4">
                    <div className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">Admin/RBAC changes</div>
                    <div className="mt-1 font-heading font-bold text-2xl text-violet-600">
                        {byAction.filter((a) => a.action.startsWith("admin.")).reduce((s, a) => s + a.count, 0)}
                    </div>
                </div>
            </div>

            {/* Top actors */}
            {summary?.top_actors?.length > 0 && (
                <div className="card-soft p-4 mb-4">
                    <div className="flex items-center gap-2 mb-2 text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">
                        <Users size={12} /> Top actors ({days}d)
                    </div>
                    <div className="flex flex-wrap gap-2">
                        {summary.top_actors.map((a) => (
                            <Badge key={a.actor} className="border border-border bg-muted/40 text-foreground text-[11px]" data-testid={`cc-actor-${a.actor}`}>
                                {a.actor} <span className="ml-1 text-muted-foreground">· {a.count}</span>
                            </Badge>
                        ))}
                    </div>
                </div>
            )}

            {/* Filters */}
            <div className="card-soft p-4 mb-4 grid grid-cols-1 md:grid-cols-4 gap-3">
                <Select value={String(days)} onValueChange={(v) => setDays(parseInt(v, 10))}>
                    <SelectTrigger className="h-9" data-testid="cc-filter-days"><SelectValue /></SelectTrigger>
                    <SelectContent>
                        {DAY_OPTIONS.map((o) => <SelectItem key={o.v} value={String(o.v)}>{o.l}</SelectItem>)}
                    </SelectContent>
                </Select>
                <Select value={action} onValueChange={setAction}>
                    <SelectTrigger className="h-9" data-testid="cc-filter-action"><SelectValue placeholder="Any action" /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="any">Any sensitive action</SelectItem>
                        {watched.map((w) => <SelectItem key={w.action} value={w.action}>{w.label}</SelectItem>)}
                    </SelectContent>
                </Select>
                <Input
                    placeholder="Actor filter (email contains…)"
                    value={actorFilter}
                    onChange={(e) => setActorFilter(e.target.value)}
                    data-testid="cc-filter-actor"
                    className="h-9"
                />
                <div className="text-[11px] text-muted-foreground inline-flex items-center gap-1 self-center">
                    <Filter size={11} /> {events.length} event{events.length === 1 ? "" : "s"} in view
                </div>
            </div>

            {/* Events table */}
            {loading ? (
                <div className="py-16 flex justify-center"><Loader2 size={18} className="animate-spin text-emerald-500" /></div>
            ) : events.length === 0 ? (
                <div className="card-soft p-10 text-center">
                    <FileCheck size={22} className="mx-auto text-muted-foreground" />
                    <div className="mt-2 font-heading font-semibold">No sensitive events in this window.</div>
                    <div className="mt-1 text-xs text-muted-foreground">Adjust the filters or widen the time range.</div>
                </div>
            ) : (
                <div className="card-soft overflow-hidden">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="border-b border-border text-left text-[11px] uppercase text-muted-foreground">
                                <th className="p-3 font-semibold">When</th>
                                <th className="p-3 font-semibold">Action</th>
                                <th className="p-3 font-semibold">Actor</th>
                                <th className="p-3 font-semibold">Target</th>
                                <th className="p-3 font-semibold">Justification / meta</th>
                                <th className="p-3 font-semibold">IP</th>
                            </tr>
                        </thead>
                        <tbody>
                            {events.map((e, idx) => (
                                <tr key={`${e.at}-${idx}`} className="border-b border-border/60 last:border-0 hover:bg-muted/30" data-testid={`cc-row-${idx}`}>
                                    <td className="p-3 text-xs text-muted-foreground whitespace-nowrap">
                                        {e.at ? new Date(e.at).toLocaleString() : "—"}
                                    </td>
                                    <td className="p-3">
                                        <Badge className={`text-[10px] border ${actionTone(e.action)}`}>{e.label || e.action}</Badge>
                                    </td>
                                    <td className="p-3 text-xs text-foreground truncate max-w-[220px]">{e.actor || "system"}</td>
                                    <td className="p-3 text-[11px] mono-score text-muted-foreground truncate max-w-[160px]" title={e.target}>{e.target || "—"}</td>
                                    <td className="p-3 text-xs text-foreground max-w-[380px]">
                                        {e.meta?.reason ? (
                                            <span className="italic">&ldquo;{String(e.meta.reason).slice(0, 200)}&rdquo;</span>
                                        ) : e.meta?.fields ? (
                                            <span className="text-muted-foreground">fields: {(e.meta.fields || []).join(", ")}</span>
                                        ) : e.meta?.role ? (
                                            <span className="text-muted-foreground">role: {e.meta.role}</span>
                                        ) : (
                                            <span className="text-muted-foreground/70">—</span>
                                        )}
                                    </td>
                                    <td className="p-3 text-[11px] mono-score text-muted-foreground">{e.ip || "—"}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
                </div>
            )}
        </div>
    );
}
